/**
 * Enhanced Machine Prestart — built for MCS (Mobile Conveying Services).
 * Adds detailed fluid levels, conditions, damage tracking + photos on top of
 * the standard prestart. Activated automatically when company.code === "MCS"
 * OR company.settings.enhanced_prestart === true.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, X, AlertTriangle, Camera, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUEL_OPTIONS = [
  { value: 0, label: "Empty" },
  { value: 0.25, label: "¼" },
  { value: 0.5, label: "½" },
  { value: 0.75, label: "¾" },
  { value: 1.0, label: "Full" },
];
const LEVEL_OPTIONS = ["low", "ok", "full"];
const CONDITION_OPTIONS = ["good", "ok", "poor"];
const BELT_OPTIONS = ["good", "ok", "damaged", "n_a"];

const DEFAULT_CHECKLIST = [
  "Tyre / track condition",
  "Lights and indicators",
  "Horns and reverse beepers",
  "Fire extinguisher",
  "Seat belt / restraints",
];

export const EnhancedPrestartModal = ({ machine, onClose, onSubmitted }) => {
  const [hours, setHours] = useState(machine.current_hours || "");
  const [kms, setKms] = useState(machine.current_km || "");
  const [notes, setNotes] = useState("");
  const [fuelLevel, setFuelLevel] = useState(null);
  const [engineOilLevel, setEngineOilLevel] = useState("ok");
  const [engineOilCondition, setEngineOilCondition] = useState("good");
  const [hydraulicOilLevel, setHydraulicOilLevel] = useState("ok");
  const [hydraulicOilCondition, setHydraulicOilCondition] = useState("good");
  const [coolantLevel, setCoolantLevel] = useState("ok");
  const [greasingDone, setGreasingDone] = useState(false);
  const [beltCondition, setBeltCondition] = useState("n_a");
  const [damageReported, setDamageReported] = useState(false);
  const [damageDescription, setDamageDescription] = useState("");
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [checklist, setChecklist] = useState(
    DEFAULT_CHECKLIST.reduce((acc, it) => ({ ...acc, [it]: true }), {})
  );
  const [submitting, setSubmitting] = useState(false);

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    const dataUrls = await Promise.all(files.map(f => new Promise(res => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setDamagePhotos(prev => [...prev, ...dataUrls]);
  };

  const removePhoto = (i) => setDamagePhotos(p => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!hours && hours !== 0) {
      toast.error("Hours required");
      return;
    }
    if (damageReported && !damageDescription.trim()) {
      toast.error("Describe the damage before submitting");
      return;
    }
    setSubmitting(true);
    try {
      const cl = Object.entries(checklist).map(([item, ok]) => ({ item, ok }));
      const allOk = cl.every(c => c.ok) && !damageReported &&
        fuelLevel !== null && fuelLevel > 0.25 &&
        engineOilLevel !== "low" && hydraulicOilLevel !== "low" && coolantLevel !== "low" &&
        engineOilCondition !== "poor" && hydraulicOilCondition !== "poor" &&
        beltCondition !== "damaged";
      const res = await axios.post(`${API}/machine-prestarts`, {
        machine_id: machine.machine_id || machine.id,
        hours: parseFloat(hours),
        kilometres: kms ? parseFloat(kms) : null,
        notes: notes || null,
        checklist: cl,
        prestart_passed: allOk,
        fuel_level: fuelLevel,
        engine_oil_level: engineOilLevel,
        engine_oil_condition: engineOilCondition,
        hydraulic_oil_level: hydraulicOilLevel,
        hydraulic_oil_condition: hydraulicOilCondition,
        coolant_level: coolantLevel,
        greasing_done: greasingDone,
        belt_condition: beltCondition,
        damage_reported: damageReported,
        damage_description: damageDescription || null,
        damage_photo_urls: damagePhotos,
      });
      const alerts = res.data?.alerts || [];
      if (alerts.length > 0) {
        toast.warning(`Prestart logged with ${alerts.length} alert(s) → admin notified`);
      } else {
        toast.success(`Prestart submitted for ${machine.name || machine.rego}`);
      }
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit prestart");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="enhanced-prestart-modal">
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[95vh] flex flex-col shadow-2xl">
        <div className="border-b-2 p-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold uppercase flex items-center gap-2">
            <Wrench className="w-5 h-5 text-orange-500" />
            Prestart — {machine.name || machine.rego}
          </h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Hours / Kms */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Current Hours *</label>
              <input type="number" step="0.1" value={hours} onChange={e => setHours(e.target.value)}
                className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="prestart-hours" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Current Kms</label>
              <input type="number" step="0.1" value={kms} onChange={e => setKms(e.target.value)}
                className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="prestart-kms" />
            </div>
          </div>

          {/* Fuel level — quarter increments */}
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">Fuel Level *</label>
            <div className="grid grid-cols-5 gap-1">
              {FUEL_OPTIONS.map(o => (
                <button key={o.value} type="button" onClick={() => setFuelLevel(o.value)}
                  className={`px-2 py-2 rounded font-bold text-sm border-2 transition-colors ${
                    fuelLevel === o.value ? "bg-orange-500 text-white border-orange-600" : "bg-white border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`fuel-${o.value}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fluids */}
          <FluidRow label="Engine Oil" level={engineOilLevel} setLevel={setEngineOilLevel}
            condition={engineOilCondition} setCondition={setEngineOilCondition} testidBase="engine-oil" />
          <FluidRow label="Hydraulic Oil" level={hydraulicOilLevel} setLevel={setHydraulicOilLevel}
            condition={hydraulicOilCondition} setCondition={setHydraulicOilCondition} testidBase="hydraulic-oil" />
          <FluidRow label="Coolant" level={coolantLevel} setLevel={setCoolantLevel} testidBase="coolant" />

          {/* Greasing */}
          <label className="flex items-center gap-2 cursor-pointer p-2 bg-slate-50 rounded">
            <input type="checkbox" checked={greasingDone} onChange={e => setGreasingDone(e.target.checked)} className="w-5 h-5" data-testid="greasing-done" />
            <span className="text-sm font-semibold">Greasing done today</span>
          </label>

          {/* Belt condition */}
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Belt Condition (conveyors)</label>
            <div className="grid grid-cols-4 gap-1">
              {BELT_OPTIONS.map(o => (
                <button key={o} type="button" onClick={() => setBeltCondition(o)}
                  className={`px-2 py-2 rounded text-sm font-semibold border-2 capitalize ${
                    beltCondition === o
                      ? (o === "damaged" ? "bg-red-600 text-white border-red-700" : "bg-orange-500 text-white border-orange-600")
                      : "bg-white border-slate-200"
                  }`}
                  data-testid={`belt-${o}`}>
                  {o === "n_a" ? "N/A" : o}
                </button>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">General Checks</label>
            <div className="space-y-1">
              {DEFAULT_CHECKLIST.map(item => (
                <label key={item} className="flex items-center gap-2 cursor-pointer text-sm bg-slate-50 rounded px-2 py-1">
                  <input type="checkbox" checked={checklist[item]} onChange={e => setChecklist({ ...checklist, [item]: e.target.checked })} className="w-4 h-4" />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Damage report */}
          <div className={`border-2 rounded p-3 ${damageReported ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={damageReported} onChange={e => setDamageReported(e.target.checked)} className="w-5 h-5" data-testid="damage-toggle" />
              <span className="text-sm font-bold flex items-center gap-1">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                Damage to report
              </span>
            </label>
            {damageReported && (
              <>
                <textarea value={damageDescription} onChange={e => setDamageDescription(e.target.value)} rows={2}
                  placeholder="Describe the damage" className="w-full border-2 border-red-200 rounded px-3 py-2 text-sm mb-2" data-testid="damage-description" />
                <label className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer">
                  <Camera className="w-4 h-4" /> Add photos
                  <input type="file" accept="image/*" multiple onChange={onPhoto} className="hidden" data-testid="damage-photos-input" />
                </label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {damagePhotos.map((url, i) => (
                    <div key={i} className="relative border-2 border-red-200 rounded overflow-hidden">
                      <img src={url} alt={`Damage ${i + 1}`} className="w-full h-20 object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-red-600 text-white rounded p-1"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Any other notes" className="w-full border-2 border-slate-200 rounded px-3 py-2 text-sm" data-testid="prestart-notes" />
        </div>

        <div className="border-t-2 p-4 flex gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
          <button onClick={submit} disabled={submitting} className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded font-bold" data-testid="submit-prestart">
            {submitting ? "Submitting…" : "Submit Prestart"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FluidRow = ({ label, level, setLevel, condition, setCondition, testidBase }) => (
  <div className="space-y-1">
    <label className="block text-xs font-semibold uppercase text-slate-500">{label}</label>
    <div className="flex gap-2 items-center">
      <div className="flex gap-1 flex-1">
        {LEVEL_OPTIONS.map(l => (
          <button key={l} type="button" onClick={() => setLevel(l)}
            className={`flex-1 px-2 py-2 rounded text-xs font-bold border-2 capitalize ${
              level === l
                ? (l === "low" ? "bg-red-500 text-white border-red-600" : "bg-orange-500 text-white border-orange-600")
                : "bg-white border-slate-200"
            }`}
            data-testid={`${testidBase}-level-${l}`}>
            {l}
          </button>
        ))}
      </div>
      {setCondition && (
        <div className="flex gap-1 flex-1">
          {CONDITION_OPTIONS.map(c => (
            <button key={c} type="button" onClick={() => setCondition(c)}
              className={`flex-1 px-2 py-2 rounded text-xs font-bold border-2 capitalize ${
                condition === c
                  ? (c === "poor" ? "bg-red-500 text-white border-red-600" : "bg-slate-700 text-white border-slate-800")
                  : "bg-white border-slate-200"
              }`}
              data-testid={`${testidBase}-condition-${c}`}>
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default EnhancedPrestartModal;
